import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { getTaobaoApiUsageEvents } from '../../api/taobaoIntegration';
import './index.css';

const { Text, Title } = Typography;

const OUTCOME_PRESENTATION = {
  SUCCESS: { label: '成功', color: 'success' },
  FAILED: { label: '失败', color: 'error' },
  BUDGET_BLOCKED: { label: '预算阻断', color: 'warning' },
};

const initialFilters = (searchParams) => {
  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const userId = Number(searchParams.get('userId'));
  return ({
    dateRange: [from && dayjs(from).isValid() ? dayjs(from) : dayjs().subtract(6, 'day'),
      to && dayjs(to).isValid() ? dayjs(to) : dayjs()],
    userId: Number.isInteger(userId) && userId > 0 ? userId : null,
    method: searchParams.get('method') || '',
    caller: searchParams.get('caller') || '',
    operationId: searchParams.get('operationId') || '',
  });
};

const formatTime = (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';

const TaobaoApiUsage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => initialFilters(searchParams));
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [result, setResult] = useState({ list: [], total: 0, page: 1, size: 20 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const requestParams = useMemo(() => ({
    from: appliedFilters.dateRange?.[0]?.format('YYYY-MM-DD'),
    to: appliedFilters.dateRange?.[1]?.format('YYYY-MM-DD'),
    userId: appliedFilters.userId || undefined,
    method: appliedFilters.method.trim() || undefined,
    caller: appliedFilters.caller.trim() || undefined,
    operationId: appliedFilters.operationId.trim() || undefined,
    page,
    size: pageSize,
  }), [appliedFilters, page, pageSize]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getTaobaoApiUsageEvents(requestParams);
      setResult(response || { list: [], total: 0, page, size: pageSize });
    } catch (requestError) {
      setError(requestError?.message || '淘宝调用明细加载失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, requestParams]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const applyFilters = () => {
    if (!filters.dateRange?.[0] || !filters.dateRange?.[1]) {
      setError('请选择完整的查询日期');
      return;
    }
    if (filters.dateRange[1].diff(filters.dateRange[0], 'day') > 89) {
      setError('查询时间范围不能超过 90 天');
      return;
    }
    const nextFilters = {
      ...filters,
      method: filters.method.trim(),
      caller: filters.caller.trim(),
      operationId: filters.operationId.trim(),
    };
    setPage(1);
    setAppliedFilters(nextFilters);
    const params = {
      from: nextFilters.dateRange[0].format('YYYY-MM-DD'),
      to: nextFilters.dateRange[1].format('YYYY-MM-DD'),
    };
    if (nextFilters.userId) params.userId = String(nextFilters.userId);
    if (nextFilters.method) params.method = nextFilters.method;
    if (nextFilters.caller) params.caller = nextFilters.caller;
    if (nextFilters.operationId) params.operationId = nextFilters.operationId;
    setSearchParams(params);
  };

  const columns = useMemo(() => [
    {
      title: '调用时间',
      dataIndex: 'occurredAt',
      key: 'occurredAt',
      width: 168,
      render: formatTime,
    },
    {
      title: 'APP 用户',
      dataIndex: 'userId',
      key: 'userId',
      width: 112,
      render: (value) => value ? `用户 #${value}` : '系统/匿名',
    },
    {
      title: '调用场景',
      dataIndex: 'caller',
      key: 'caller',
      width: 150,
      render: (value, record) => (
        <div className="taobao-usage-scene">
          <strong>{value || '-'}</strong>
          <Text type="secondary">{record.scene || '-'}</Text>
        </div>
      ),
    },
    {
      title: '淘宝 TOP 方法',
      dataIndex: 'topMethod',
      key: 'topMethod',
      width: 260,
    },
    {
      title: '请求结果',
      dataIndex: 'outcome',
      key: 'outcome',
      width: 112,
      render: (value, record) => {
        const presentation = OUTCOME_PRESENTATION[value] || { label: value || '未知', color: 'default' };
        return (
          <Space direction="vertical" size={2}>
            <Tag color={presentation.color}>{presentation.label}</Tag>
            <Text type="secondary">{record.sentUpstream ? '已请求淘宝' : '未请求淘宝'}</Text>
          </Space>
        );
      },
    },
    {
      title: '尝试 / 预算',
      key: 'budget',
      width: 132,
      render: (_, record) => (
        <div className="taobao-usage-budget">
          <span>第 {record.attemptNo || 0} 次</span>
          <Text type="secondary">
            {record.materialBudget || 0} + {record.detailBudget || 0} / {record.totalBudget || 0}
          </Text>
        </div>
      ),
    },
    {
      title: '耗时',
      dataIndex: 'durationMs',
      key: 'durationMs',
      width: 90,
      render: (value) => `${value || 0} ms`,
    },
  ], []);

  return (
    <div className="taobao-usage-page">
      <header className="taobao-usage-header">
        <div>
          <Title level={2}>淘宝调用明细</Title>
          <Text type="secondary">追踪实际淘宝请求、预算阻断及对应 APP 用户</Text>
        </div>
        <Button aria-label="刷新调用明细" icon={<ReloadOutlined />} loading={loading} onClick={loadEvents} />
      </header>

      <section className="taobao-usage-filter" aria-label="淘宝调用筛选">
        <DatePicker.RangePicker
          value={filters.dateRange}
          format="YYYY-MM-DD"
          allowClear={false}
          onChange={(dateRange) => setFilters((previous) => ({ ...previous, dateRange }))}
        />
        <InputNumber
          aria-label="APP 用户 ID"
          placeholder="APP 用户 ID"
          min={1}
          precision={0}
          value={filters.userId}
          onChange={(userId) => setFilters((previous) => ({ ...previous, userId }))}
        />
        <Input
          aria-label="淘宝 TOP 方法"
          placeholder="淘宝 TOP 方法"
          value={filters.method}
          onChange={(event) => setFilters((previous) => ({ ...previous, method: event.target.value }))}
        />
        <Input
          aria-label="调用场景"
          placeholder="调用场景 caller"
          value={filters.caller}
          onChange={(event) => setFilters((previous) => ({ ...previous, caller: event.target.value }))}
        />
        <Input
          aria-label="操作 ID"
          placeholder="操作 ID"
          value={filters.operationId}
          onChange={(event) => setFilters((previous) => ({ ...previous, operationId: event.target.value }))}
          onPressEnter={applyFilters}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters}>查询</Button>
      </section>

      {error ? <Alert showIcon type="error" message={error} /> : null}

      <section className="taobao-usage-table" aria-label="淘宝调用事件列表">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={result.list || []}
          scroll={{ x: 1120 }}
          locale={{ emptyText: <Empty description="当前条件暂无淘宝调用记录" /> }}
          pagination={{
            current: page,
            pageSize,
            total: Number(result.total || 0),
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (nextPage, nextSize) => {
              setPage(nextSize !== pageSize ? 1 : nextPage);
              setPageSize(nextSize);
            },
          }}
          expandable={{
            expandedRowRender: (record) => (
              <dl className="taobao-usage-detail">
                <div><dt>操作 ID</dt><dd>{record.operationId || '-'}</dd></div>
                <div><dt>APP 请求 ID</dt><dd>{record.appRequestId || '-'}</dd></div>
                <div><dt>淘宝请求 ID</dt><dd>{record.upstreamRequestId || '-'}</dd></div>
                <div><dt>HTTP 状态</dt><dd>{record.httpStatus || '-'}</dd></div>
                <div><dt>错误码</dt><dd>{record.errorCode || record.subCode || '-'}</dd></div>
              </dl>
            ),
            rowExpandable: () => true,
          }}
        />
      </section>
    </div>
  );
};

export default TaobaoApiUsage;
